import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Autocomplete from "@mui/material/Autocomplete";
import AutocompleteTextField from "../AutocompleteTextField/AutoCompleteTextField";
import Paper from "@mui/material/Paper";
import { SecurityService } from "../../../services/SecurityService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function UserAutocomplete({
  label,
  title,
  titleSize,
  width,
  stateOnly,
  defaultInput,
  updateSelectedUser,
  disabled,
  error,
  defaultSelected,
  openStatus,
  enableUserRegions,
  enableUserIdMcld,
  placeholder
}) {
  const { instance } = useMsal();
  const [userOptions, setUserOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [disabledAutocomplete, setDisabledAutocomplete] = useState(false);
  const { warning } = useContext(AlertContext);

  const getOptionText = option => {
    if (option && enableUserRegions && option.region) {
      return `${option.userName} - ${option.region.regionName}`;
    } else if (option && enableUserIdMcld) {
      return ` ${option.userName} - ${option.userIdMcld}`;
    } else if (option) {
      return `${option.userName}`;
    } else {
      return "";
    }
  };

  useEffect(() => {
    setSelected(defaultSelected === null ? "" : defaultSelected);
    setSearchInput(defaultInput);
    setDisabledAutocomplete(disabled);
  }, []);

  useEffect(() => {
    if (searchInput && searchInput.length > 2) {
      setIsLoading(true);

      if (enableUserRegions) {
        SecurityService.GetUserByNameOrRegion(instance, searchInput).then(res => {
          setUserOptions(res);
          setIsLoading(false);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
      } else if (enableUserIdMcld) {
        SecurityService.GetUsers(instance).then(res => {
          setUserOptions(res.filter(({ userName, userIdMcld }) =>
            userName.toLowerCase().includes(searchInput.toLowerCase().trim()) ||
            userIdMcld.includes(searchInput.toLowerCase().trim())));
          setIsLoading(false);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
      } else {
        SecurityService.GetUserByName(instance, searchInput).then(res => {
          setUserOptions(res);
          setIsLoading(false);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
      }
    }
  }, [searchInput]);

  useEffect(() => {
    updateSelectedUser(selected);
    if (selected == null || selected == "") setUserOptions([]);
  }, [selected]);

  useEffect(() => {
    if (openStatus) {
      setSelected(defaultSelected);
      setSearchInput(defaultInput);
      setDisabledAutocomplete(true);
    } else {
      setDisabledAutocomplete(false);
    }
  }, [openStatus]);

  return (
    <Autocomplete
      id={`autocomplete-${label}`}
      freeSolo
      autoHighlight
      value={selected ? selected : null}
      onChange={(event, newValue) => {
        setSelected(newValue);
      }}
      options={userOptions}
      getOptionLabel={option =>
        getOptionText(option)
      }
      filterOptions={(x) => x}
      sx={{
        width: width || "300px",
        "& .MuiOutlinedInput-root": {
          padding: "1.5px 39px 1.5px 0 !important",
        }
      }}
      inputValue={searchInput ? searchInput === "undefined" ? "" : searchInput : ""}
      PaperComponent={
        ({ children }) =>
          <Paper sx={{
            backgroundColor: "black_solid.10",
            fontSize: "body1.fontSize",
            fontWeight: "body1.fontWeight"
          }}
          >
            {children}
          </Paper>
      }
      onInputChange={(event, newInputValue) => {
        setSearchInput(newInputValue);
      }}
      onKeyDown={event => event.key === "Backspace" && setSelected("")}
      disabled={disabledAutocomplete}
      renderInput={(params) =>
        <AutocompleteTextField
          label={label}
          title={title}
          titleSize={titleSize}
          isLoading={isLoading}
          searchInput={searchInput ? searchInput : ""}
          selected={selected}
          params={params}
          error={error}
          maxLength={stateOnly ? 2 : null}
          placeholder={placeholder}
        />
      }
    />
  );
}

UserAutocomplete.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.string,
  width: PropTypes.string,
  allowState: PropTypes.bool,
  stateOnly: PropTypes.bool,
  typeRequired: PropTypes.bool,
  defaultInput: PropTypes.string,
  defaultSelected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  updateSelectedUser: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  openStatus: PropTypes.bool,
  enableUserRegions: PropTypes.bool,
  enableUserIdMcld: PropTypes.bool,
  placeholder: PropTypes.string
};